import {
  useMemo,
  useState,
  useEffect,
  useCallback,
  ReactNode,
  useRef,
} from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  createColumnHelper,
  ColumnDef,
  Row,
  ColumnPinningState,
} from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStars,
  faTriangleExclamation,
} from "@fortawesome/pro-light-svg-icons";
import { fullFormatNumbers } from "../../helpers/functions/formatNumber";
import { useAppDispatch, useAppSelector } from "../../stores/hooks/hooks";
import { getPlatformIcon } from "../../helpers/functions/getPlatformIcon.tsx";
import { Menu, MenuItems, MenuItem, MenuButton } from "@headlessui/react";
import creatorBriefsEmpty from "/images/empty_states/creatorBriefs-empty.svg";
import { CreateBriefDialog } from "./components/createBriefDialog/CreateBriefDialog.tsx";
import { formatDateToMonthDayYear } from "../../helpers/functions/formatDate.tsx";
import {
  CreatorBrief,
  CreatorPlatform,
} from "../../stores/types/creatorBriefsTypes.tsx";
import {
  fetchCreatorBriefsThunk,
  deleteCreatorBriefThunk,
} from "../../stores/thunks/creatorBriefsThunk.tsx";
import { DeleteBriefDialog } from "./components/DeleteBriefDialog.tsx";
import {
  RevActionsHeader,
  RevAlert,
  RevAvatar,
  RevButton,
  RevEmptyPage,
  RevLink,
  RevSpinner,
  RevTable,
  RevTooltip,
  RevActionButton,
  type RevTablePaginationType,
} from "@revmatics/catalyst";
import { pollForBriefGeneration } from "../../helpers/services/pollingService.ts";
import { fetchPersonasData } from "../../stores/thunks/personasThunk.tsx";
import { displayAlert } from "../../stores/slice/alertSlice.tsx";

const createStyledHeader = (title: string): (() => ReactNode) => {
  return () => (
    <span className="whitespace-nowrap typography-body-bold-xs text-monochrome-900">
      {title}
    </span>
  );
};

const GeneratingCell = () => (
  <span className="typography-body-sm text-monochrome-500">Generating...</span>
);

const BriefNameCell = ({
  row,
  isPolling,
}: {
  row: Row<CreatorBrief>;
  isPolling: boolean;
}) => {
  const { id, title, is_ai_generated, status } = row.original;

  if (status === "processing" || isPolling) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-monochrome-900">{title}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <RevLink pathname={`/creator-briefs/${id}`} text={title} />
      {is_ai_generated && (
        <RevTooltip
          position="right"
          className="whitespace-normal w-[240px] xl:w-[400px]"
          content="Revmatics automatically generated this Creator Brief from your data and other sources."
        >
          <FontAwesomeIcon
            icon={faStars}
            className="typography-body-md text-purple-600"
          />
        </RevTooltip>
      )}
    </div>
  );
};

const CreatorCell = ({ row }: { row: Row<CreatorBrief> }) => {
  const { creator } = row.original;
  const imageUrl = creator.creator_image_file || creator.creator_image_link;
  const [showFallback, setShowFallback] = useState(!imageUrl);

  useEffect(() => {
    setShowFallback(!imageUrl);
  }, [imageUrl]);

  return (
    <div className="flex items-center gap-3">
      {showFallback ? (
        <RevAvatar
          variant="circle"
          size="medium"
          theme="light"
          firstName={creator.name?.split(" ")[0]}
          lastName={creator.name?.split(" ").pop()}
        />
      ) : (
        <img
          src={imageUrl || ""}
          alt={creator.name}
          className="h-10 w-10 rounded-full object-cover flex-shrink-0"
          onError={() => setShowFallback(true)}
        />
      )}
      <span className="typography-body-sm text-monochrome-900">
        {creator.name}
      </span>
    </div>
  );
};

const PersonaCell = ({ row }: { row: Row<CreatorBrief> }) => {
  const { personaDetails: ffPersonaDetails } = useFlags();
  const { persona } = row.original;
  const { personas: allPersonasData } = useAppSelector(
    (state) => state.personas,
  );
  const fullPersonaData = useMemo(
    () => allPersonasData?.find((p) => p.id === persona.id),
    [allPersonasData, persona.id],
  );
  const personaGender = fullPersonaData?.gender;

  const imageUrl = persona.persona_picture || persona.persona_picture_url;
  const [showFallback, setShowFallback] = useState(!imageUrl);

  useEffect(() => {
    setShowFallback(!imageUrl);
  }, [imageUrl]);

  return (
    <div className="flex items-center gap-3">
      {showFallback ? (
        <div className="h-10 w-10 rounded-full bg-monochrome-50 flex items-center justify-center flex-shrink-0">
          <RevAvatar
            variant="circle"
            size="medium"
            theme="light"
            firstName={persona.name?.split(" ")[0]}
            lastName={persona.name?.split(" ").pop()}
            gender={personaGender}
          />
        </div>
      ) : (
        <div className="h-10 w-10 rounded-full flex-shrink-0 overflow-hidden">
          <img
            src={imageUrl || ""}
            alt={persona.name}
            className="w-full h-full object-cover"
            onError={() => setShowFallback(true)}
          />
        </div>
      )}
      <div className="flex items-center gap-1">
        {ffPersonaDetails ? (
          <RevLink
            pathname=""
            search={`personaId=${persona.id}`}
            text={persona.name}
          />
        ) : (
          <span className="typography-body-sm">{persona.name}</span>
        )}
        {persona.is_ai_generated && (
          <RevTooltip
            position="top"
            className="w-[330px]"
            content="This persona was automatically generated by our platform."
          >
            <FontAwesomeIcon
              icon={faStars}
              className="typography-body-md text-purple-600 ml-1"
            />
          </RevTooltip>
        )}
      </div>
    </div>
  );
};

const columnHelper = createColumnHelper<CreatorBrief>();
const BRIEFS_SAVED_TABLE = "briefsTable";

export const CreatorBriefsListPage = () => {
  const dispatch = useAppDispatch();

  const {
    briefs: briefsDataFromStore,
    isLoading,
    isDeleting,
  } = useAppSelector((state) => state.creatorBriefs);
  const { isTutorialCreatorBriefs: ffTutorialCreatorBriefs } = useFlags();

  const { personas: personasDataFromStore } = useAppSelector(
    (state) => state.personas,
  );

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [briefToDelete, setBriefToDelete] = useState<CreatorBrief | null>(null);
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>({
    right: ["actions"],
  });
  const [isCreateBriefDialogOpen, setIsCreateBriefDialogOpen] = useState(false);
  const [pollingBriefIds, setPollingBriefIds] = useState<Set<number>>(
    new Set(),
  );
  const pollingControllers = useRef(new Map<number, AbortController>());

  const startPollingBrief = useCallback(
    (brief: CreatorBrief) => {
      const briefId = brief.id;
      if (pollingControllers.current.has(briefId)) {
        return;
      }

      setPollingBriefIds((prev) => {
        const newSet = new Set(prev);
        newSet.add(briefId);
        return newSet;
      });

      const controller = new AbortController();
      pollingControllers.current.set(briefId, controller);

      pollForBriefGeneration(briefId, { signal: controller.signal })
        .then(() => {
          dispatch(
            displayAlert({
              type: "success",
              text: `Creator Brief "${brief.title}" is ready.`,
            }),
          );
        })
        .catch((err) => {
          if (err.name !== "AbortError") {
            console.error(`Polling for brief ${briefId} failed:`, err);
          }
        })
        .finally(() => {
          setPollingBriefIds((prev) => {
            const newSet = new Set(prev);
            newSet.delete(briefId);
            return newSet;
          });
          pollingControllers.current.delete(briefId);
          dispatch(fetchCreatorBriefsThunk());
        });
    },
    [dispatch],
  );

  const handleCreationSuccess = useCallback(() => {
    setIsCreateBriefDialogOpen(false);
    dispatch(fetchCreatorBriefsThunk());
  }, [dispatch]);

  useEffect(() => {
    if (!briefsDataFromStore) {
      dispatch(fetchCreatorBriefsThunk());
    }
    if (!personasDataFromStore) {
      dispatch(fetchPersonasData());
    }

    if (briefsDataFromStore) {
      const processingBriefs = briefsDataFromStore.filter(
        (b) => b.status === "processing",
      );

      if (processingBriefs.length > 0) {
        processingBriefs.forEach(startPollingBrief);
      }
    }
  }, [dispatch, briefsDataFromStore, personasDataFromStore, startPollingBrief]);

  useEffect(() => {
    const controllers = pollingControllers.current;

    return () => {
      controllers.forEach((controller) => controller.abort());
    };
  }, []);

  const handleOpenDeleteDialog = useCallback((brief: CreatorBrief) => {
    setBriefToDelete(brief);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = () => {
    if (!briefToDelete) return;
    dispatch(deleteCreatorBriefThunk(briefToDelete.id))
      .unwrap()
      .catch((err) => console.error("Failed to delete creator brief:", err))
      .finally(() => {
        setIsDeleteDialogOpen(false);
        setTimeout(() => setBriefToDelete(null), 150);
      });
  };

  const isPollingActive = pollingBriefIds.size > 0;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns = useMemo<ColumnDef<CreatorBrief, any>[]>(
    () => [
      columnHelper.accessor("title", {
        header: createStyledHeader("Creator Brief Name"),
        cell: (info) => (
          <BriefNameCell
            row={info.row}
            isPolling={pollingBriefIds.has(info.row.original.id)}
          />
        ),
      }),
      columnHelper.accessor("creator", {
        header: createStyledHeader("Creator"),
        cell: (info) => <CreatorCell row={info.row} />,
      }),
      columnHelper.accessor("creator.platforms", {
        header: createStyledHeader("Platforms"),
        cell: (info) => {
          if (pollingBriefIds.has(info.row.original.id))
            return <GeneratingCell />;
          const platforms = info.getValue() as CreatorPlatform[];
          if (!platforms || platforms.length === 0) return "";
          return (
            <div className="flex items-center gap-1">
              {platforms.map((platform) => {
                const icon = getPlatformIcon(
                  platform.platform.name.toLowerCase(),
                );
                return icon ? (
                  <FontAwesomeIcon
                    key={platform.platform.id}
                    icon={icon}
                    className="text-monochrome-500 text-md"
                    title={platform.platform.name}
                  />
                ) : null;
              })}
            </div>
          );
        },
      }),
      columnHelper.accessor("campaign", {
        header: createStyledHeader("Timeline"),
        cell: (info) => {
          if (pollingBriefIds.has(info.row.original.id))
            return <GeneratingCell />;
          const campaign = info.getValue();
          if (!campaign.start_date || !campaign.end_date) return "-";
          const start = formatDateToMonthDayYear(campaign.start_date);
          const end = formatDateToMonthDayYear(campaign.end_date);
          return (
            <span>
              {start} – {end}
            </span>
          );
        },
      }),
      columnHelper.accessor("deliverables", {
        header: createStyledHeader("Deliverables"),
        cell: (info) => {
          if (pollingBriefIds.has(info.row.original.id))
            return <GeneratingCell />;
          return (
            <div className="text-right">{info.getValue()?.length || 0}</div>
          );
        },
      }),
      columnHelper.accessor("interest_group.condensed_name", {
        header: createStyledHeader("Interest Group"),
        cell: (info) => {
          if (pollingBriefIds.has(info.row.original.id))
            return <GeneratingCell />;
          return info.getValue();
        },
      }),
      columnHelper.accessor("persona", {
        header: createStyledHeader("Persona"),
        cell: (info) => <PersonaCell row={info.row} />,
      }),
      columnHelper.accessor("creator.total_community_size", {
        header: createStyledHeader("Total Community Size"),
        cell: (info) => {
          if (pollingBriefIds.has(info.row.original.id))
            return <GeneratingCell />;
          return (
            <div className="text-right">
              {fullFormatNumbers({
                value: info.getValue() || 0,
                noFormatNumber: true,
                shortenValue: 1e3,
              })}
            </div>
          );
        },
      }),
      columnHelper.accessor("brandassets", {
        header: createStyledHeader("Brand Assets"),
        cell: (info) => {
          if (pollingBriefIds.has(info.row.original.id))
            return <GeneratingCell />;
          const assets = info.getValue();
          if (!assets || assets.length === 0) {
            return (
              <div className="flex items-center gap-1.5">
                <p>None</p>
                <FontAwesomeIcon
                  icon={faTriangleExclamation}
                  className="text-yellow-500"
                />
              </div>
            );
          }
          return <span>{assets.length}</span>;
        },
      }),
      columnHelper.display({
        id: "actions",
        cell: ({ row }) => (
          <div className="text-center">
            <Menu>
              {({ open }) => {
                return (
                  <div className="relative">
                    {!open ? (
                      <RevTooltip
                        content="More Actions"
                        position="left"
                        className="w-[90px]"
                      >
                        <MenuButton as={RevActionButton} />
                      </RevTooltip>
                    ) : (
                      <MenuButton as={RevActionButton} />
                    )}

                    <MenuItems
                      transition
                      anchor="bottom end"
                      className="w-52 shadow-elevation-1 z-20 bg-white origin-top-right rounded-md py-1 text-sm/6 transition duration-100 ease-out [--anchor-gap:--spacing(1)] focus:outline-none data-closed:scale-95 data-closed:opacity-0"
                    >
                      <MenuItem>
                        <button
                          onClick={() => handleOpenDeleteDialog(row.original)}
                          className="group flex w-full items-center text-red-500 gap-2 rounded-sm px-3 py-1.5 hover:bg-monochrome-50"
                        >
                          Delete Creator Brief
                        </button>
                      </MenuItem>
                    </MenuItems>
                  </div>
                );
              }}
            </Menu>
          </div>
        ),
      }),
    ],
    [handleOpenDeleteDialog, pollingBriefIds],
  );

  const saved: RevTablePaginationType = JSON.parse(
    sessionStorage.getItem(BRIEFS_SAVED_TABLE) || "{}",
  );

  if (isLoading && !briefsDataFromStore) {
    return <RevSpinner size="large" centered color="primary" />;
  }

  if (
    !isLoading &&
    (!briefsDataFromStore || briefsDataFromStore.length === 0) &&
    !isPollingActive
  ) {
    return (
      <>
        <RevEmptyPage
          image={creatorBriefsEmpty}
          title="No Creator Briefs"
          text="Generate your first creator brief..."
          handleClick={() => setIsCreateBriefDialogOpen(true)}
          textBtn="Generate Creator Brief"
        />
        <CreateBriefDialog
          isOpen={isCreateBriefDialogOpen}
          setIsOpen={setIsCreateBriefDialogOpen}
          onSuccess={handleCreationSuccess}
        />
      </>
    );
  }

  return (
    <>
      <div>
        <RevActionsHeader
          title="Creator Briefs"
          videoBtn={ffTutorialCreatorBriefs}
          headerClassName={`${isPollingActive && `!mb-4`}`}
        >
          <RevButton
            text="Generate Creator Brief"
            displayAs="button"
            buttonSize="medium"
            handleClick={() => setIsCreateBriefDialogOpen(true)}
            className="max-w-[174px]"
          />
        </RevActionsHeader>

        {isPollingActive && (
          <div className="mb-3">
            <RevAlert
              data={{
                heading: "Generation of Creator Brief(s) in progress",
                text: "",
                type: "info",
                id: "generating-briefs-id",
                show: true,
              }}
            />
          </div>
        )}
        <RevTable<CreatorBrief>
          data={briefsDataFromStore || []}
          columns={columns}
          enableGlobalFilter={false}
          initialPageSize={saved.pageSize || 10}
          initialPageIndex={saved.pageIndex || 0}
          columnPinning={columnPinning}
          onColumnPinningChange={setColumnPinning}
          paginationSaved={BRIEFS_SAVED_TABLE}
        />
      </div>

      <DeleteBriefDialog
        isOpen={isDeleteDialogOpen}
        setIsOpen={setIsDeleteDialogOpen}
        brief={briefToDelete}
        onConfirmDelete={handleConfirmDelete}
        isDeleting={isDeleting}
      />
      <CreateBriefDialog
        isOpen={isCreateBriefDialogOpen}
        setIsOpen={setIsCreateBriefDialogOpen}
        onSuccess={handleCreationSuccess}
      />
    </>
  );
};
